import React, { useCallback, useEffect, useState } from "react";
import { useTranslate } from "react-admin";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import SnackbarUtils from "utils/SnackbarUtils";
import {
  ensureAiAuthorization,
  getAiBankCodes,
  getAiSettingContext,
  getAiSettings,
  getAiTokenStatus,
  updateAiSettings,
} from "services/aiSetting";

const useStyles = makeStyles((theme) => ({
  tabPanel: { width: "100%", margin: "0 20px", minWidth: 0 },
  page: { paddingBottom: theme.spacing(4) },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  card: { marginBottom: theme.spacing(2), borderRadius: 12 },
  sectionTitle: { fontWeight: 700, marginBottom: theme.spacing(0.5) },
  muted: { color: theme.palette.text.secondary },
  context: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
  },
  rule: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
  },
  ruleHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  fullWidth: { width: "100%" },
  helperContent: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    width: "100%",
  },
  statusRow: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap" },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

const emptyRule = () => ({
  bank_code: "",
  bank_sender_email: "",
  filename_patterns_text: ".pdf",
  pdf_password: "",
  is_active: true,
});

const toFormRule = (rule = {}) => ({
  bank_code: rule.bank_code || "",
  bank_sender_email: rule.bank_sender_email || "",
  filename_patterns_text: (rule.filename_patterns || []).join(", ") || ".pdf",
  pdf_password: "",
  has_password: Boolean(rule.has_password),
  is_active: rule.is_active !== false,
});

const toSettingsForm = (settings = {}) => ({
  enabled: Boolean(settings.enabled),
  owner_emails_text: (settings.owner_emails || []).join(", "),
  tax_ids_text: (settings.tax_ids || []).join(", "),
  rules: (settings.rules || []).map(toFormRule),
});

const splitValues = (value) =>
  (value || "")
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeBankCode = (bankCode) => (bankCode || "").trim().toUpperCase();

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.map((item) => item.message).filter(Boolean).join("<br/>");
  }
  if (Array.isArray(data?.detail)) {
    return data.detail.map((item) => item.msg).filter(Boolean).join("<br/>");
  }
  return data?.detail || data?.message || error?.message || "Request failed";
};

const AISetting = ({ value, index }) => {
  const classes = useStyles();
  const translate = useTranslate();
  const context = getAiSettingContext();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(toSettingsForm());
  const [bankCodes, setBankCodes] = useState([]);

  const loadData = useCallback(async ({ refreshOnly = false } = {}) => {
    if (!context.bu) {
      SnackbarUtils.error(translate("ra.ai.missingBu"));
      return;
    }
    setLoading(true);
    try {
      if (refreshOnly) {
        const [, currentSettings] = await Promise.all([
          getAiTokenStatus(),
          getAiSettings(),
        ]);
        setSettings(currentSettings);
        setForm(toSettingsForm(currentSettings));
        setLoaded(true);
        return;
      }

      const authorization = await ensureAiAuthorization();
      const [banks, currentSettings] = await Promise.all([
        authorization.bankCodes || getAiBankCodes(),
        getAiSettings(),
      ]);
      setBankCodes(banks);
      setSettings(currentSettings);
      setForm(toSettingsForm(currentSettings));
      setLoaded(true);
    } catch (error) {
      SnackbarUtils.error(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [context.bu, translate]);

  useEffect(() => {
    if (value === index && !loaded) loadData();
  }, [index, loadData, loaded, value]);

  const changeRule = (ruleIndex, field, nextValue) => {
    setForm((current) => ({
      ...current,
      rules: current.rules.map((rule, idx) =>
        idx === ruleIndex ? { ...rule, [field]: nextValue } : rule,
      ),
    }));
  };

  const saveSettings = async () => {
    const rules = form.rules.map((rule) => ({
      bank_code: rule.bank_code || null,
      bank_sender_email: rule.bank_sender_email.trim() || null,
      filename_patterns: splitValues(rule.filename_patterns_text),
      ...(rule.pdf_password.trim()
        ? { pdf_password: rule.pdf_password.trim() }
        : {}),
      is_active: Boolean(rule.is_active),
    }));
    if (rules.some((rule) => rule.filename_patterns.length === 0)) {
      SnackbarUtils.warning(translate("ra.ai.patternRequired"));
      return;
    }
    const selectedBankCodes = [];
    rules.forEach((rule) => {
      const bankCode = normalizeBankCode(rule.bank_code);
      if (bankCode) selectedBankCodes.push(bankCode);
    });
    if (new Set(selectedBankCodes).size !== selectedBankCodes.length) {
      SnackbarUtils.warning(translate("ra.ai.bankCodeUnique"));
      return;
    }
    const taxIds = splitValues(form.tax_ids_text);
    if (form.enabled && taxIds.length === 0) {
      SnackbarUtils.warning(translate("ra.ai.taxIdRequired"));
      return;
    }

    setSaving(true);
    try {
      const updated = await updateAiSettings({
        enabled: form.enabled,
        owner_emails: splitValues(form.owner_emails_text),
        tax_ids: taxIds,
        rules,
      });
      setSettings(updated);
      setForm(toSettingsForm(updated));
      SnackbarUtils.success(translate("ra.ai.settingsSaved"));
    } catch (error) {
      SnackbarUtils.error(getApiErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const copyIngestAddress = async () => {
    if (!settings?.ingest_address) return;
    try {
      await navigator.clipboard.writeText(settings.ingest_address);
      SnackbarUtils.success(translate("ra.ai.copied"));
    } catch (error) {
      SnackbarUtils.error(error.message);
    }
  };

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      className={classes.tabPanel}
    >
      {value === index && (
        <div className={classes.page}>
          <div className={classes.header}>
            <div>
              <Typography variant="h6">{translate("ra.ai.title")}</Typography>
              <Typography variant="body2" className={classes.muted}>
                {translate("ra.ai.subtitle")}
              </Typography>
            </div>
            <IconButton
              onClick={() => loadData({ refreshOnly: true })}
              disabled={loading}
              aria-label="refresh"
            >
              <RefreshIcon />
            </IconButton>
          </div>

          <Paper variant="outlined" className={classes.context}>
            <Typography variant="caption" display="block">URI: {context.uri}</Typography>
            <Typography variant="caption" display="block">BU: {context.bu || "-"}</Typography>
          </Paper>

          {loading && !loaded ? (
            <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>
          ) : (
            <>
              <Card variant="outlined" className={classes.card}>
                <CardContent>
                  <Typography className={classes.sectionTitle}>{translate("ra.ai.serviceStatus")}</Typography>
                  <Box className={classes.statusRow} mb={2}>
                    <Chip
                      color={settings?.status?.ready ? "primary" : "default"}
                      label={settings?.status?.ready ? translate("ra.ai.ready") : translate("ra.ai.notReady")}
                    />
                    {(settings?.status?.blockers || []).map((blocker) => (
                      <Chip key={blocker} label={blocker} variant="outlined" />
                    ))}
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth variant="outlined" size="small"
                        label={translate("ra.ai.ingestAddress")}
                        helperText={translate("ra.ai.ingestAddressHint")}
                        value={settings?.ingest_address || ""}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        fullWidth variant="contained" color="primary" startIcon={<FileCopyOutlinedIcon />}
                        disabled={!settings?.ingest_address} onClick={copyIngestAddress}
                      >
                        {translate("ra.ai.copy")}
                      </Button>
                    </Grid>
                  </Grid>
                  {settings?.gmail_confirm?.code && (
                    <Box mt={2}>
                      <Typography variant="body2">
                        {translate("ra.ai.gmailCode")}: <strong>{settings.gmail_confirm.code}</strong>
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined" className={classes.card}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <div>
                      <Typography className={classes.sectionTitle}>{translate("ra.ai.settingsTitle")}</Typography>
                      <Typography variant="body2" className={classes.muted}>{translate("ra.ai.settingsHint")}</Typography>
                    </div>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary" checked={form.enabled}
                          onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
                        />
                      }
                      label={translate("ra.ai.enabled")}
                    />
                  </Box>
                  <Divider />
                  <Box mt={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth multiline rows={2} variant="outlined"
                          label={translate("ra.ai.ownerEmails")}
                          helperText={translate("ra.ai.commaSeparated")}
                          value={form.owner_emails_text}
                          onChange={(event) => setForm((current) => ({ ...current, owner_emails_text: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth required={form.enabled} multiline rows={2} variant="outlined"
                          label={translate("ra.ai.taxIds")}
                          helperText={(
                            <span className={classes.helperContent}>
                              <span>{translate("ra.ai.taxIdHint")}</span>
                              <strong>{form.tax_ids_text.length}</strong>
                            </span>
                          )}
                          value={form.tax_ids_text}
                          onChange={(event) => setForm((current) => ({ ...current, tax_ids_text: event.target.value }))}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography className={classes.sectionTitle}>{translate("ra.ai.rules")}</Typography>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => setForm((current) => ({ ...current, rules: [...current.rules, emptyRule()] }))}
                    >
                      {translate("ra.ai.addRule")}
                    </Button>
                  </Box>

                  {form.rules.map((rule, ruleIndex) => (
                    <Paper key={ruleIndex} variant="outlined" className={classes.rule}>
                      <div className={classes.ruleHeader}>
                        <Typography variant="subtitle2">
                          {translate("ra.ai.ruleNumber", { number: ruleIndex + 1 })}
                        </Typography>
                        <IconButton
                          size="small" aria-label="delete rule"
                          onClick={() => setForm((current) => ({
                            ...current,
                            rules: current.rules.filter((unusedRule, idx) => idx !== ruleIndex),
                          }))}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </div>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <FormControl variant="outlined" size="small" className={classes.fullWidth}>
                            <InputLabel>{translate("ra.ai.bank")}</InputLabel>
                            <Select
                              value={rule.bank_code}
                              onChange={(event) => changeRule(ruleIndex, "bank_code", event.target.value)}
                              label={translate("ra.ai.bank")}
                            >
                              {bankCodes.map((bank) => (
                                <MenuItem
                                  key={bank.code}
                                  value={bank.code}
                                  disabled={form.rules.some(
                                    (otherRule, otherRuleIndex) =>
                                      otherRuleIndex !== ruleIndex &&
                                      normalizeBankCode(otherRule.bank_code) === normalizeBankCode(bank.code),
                                  )}
                                >
                                  {bank.code} - {bank.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth variant="outlined" size="small" type="email"
                            label={translate("ra.ai.senderEmail")} value={rule.bank_sender_email}
                            onChange={(event) => changeRule(ruleIndex, "bank_sender_email", event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth variant="outlined" size="small" type="password" autoComplete="new-password"
                            label={translate("ra.ai.pdfPassword")} value={rule.pdf_password}
                            placeholder={rule.has_password ? translate("ra.ai.passwordUnchanged") : ""}
                            onChange={(event) => changeRule(ruleIndex, "pdf_password", event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={9}>
                          <TextField
                            fullWidth required variant="outlined" size="small"
                            label={translate("ra.ai.filenamePatterns")}
                            helperText={translate("ra.ai.commaSeparated")}
                            value={rule.filename_patterns_text}
                            onChange={(event) => changeRule(ruleIndex, "filename_patterns_text", event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                color="primary" checked={rule.is_active}
                                onChange={(event) => changeRule(ruleIndex, "is_active", event.target.checked)}
                              />
                            }
                            label={translate("ra.ai.active")}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}

                  <div className={classes.actions}>
                    <Button variant="outlined" onClick={() => setForm(toSettingsForm(settings))} disabled={saving}>
                      {translate("ra.ai.cancel")}
                    </Button>
                    <Button variant="contained" color="primary" onClick={saveSettings} disabled={saving || loading}>
                      {saving ? <CircularProgress size={20} /> : translate("ra.ai.saveSettings")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AISetting;
